import { Capacitor } from "@capacitor/core";

/**
 * Ouverture des liens qui sortent de l'application.
 *
 * Sur mobile on passe par @capacitor/browser, qui utilise SFSafariViewController (iOS) et
 * Chrome Custom Tabs (Android) : la page s'ouvre PAR-DESSUS l'app, avec un bouton
 * « OK / Fermer » qui ramène en un seul geste. C'est le comportement attendu par Apple
 * pour un lien de politique de confidentialité, qui doit rester accessible et non piégeant.
 *
 * L'ancien `window.open(url, "_system")` éjectait vers Safari : l'utilisateur devait passer
 * par le sélecteur d'apps pour revenir, ce qui casse le parcours.
 *
 * Sur le web (build Vercel), on garde un onglet neutre avec `noopener,noreferrer`.
 */
export const openExternal = async (url: string): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url, presentationStyle: "popover" });
      return;
    } catch {
      // Plugin indisponible : on retombe sur le comportement navigateur plutôt que
      // de laisser le lien sans effet.
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Fiche de l'app dans la boutique. Ici on VEUT sortir de l'app : remise au
 * système, l'adresse ouvre l'App Store ou Google Play directement, là où le
 * navigateur intégré de `openExternal` afficherait la page web de la boutique.
 */
export const openStorePage = (url: string): void => {
  window.open(url, "_system");
};

/**
 * Page de prise de démo du site marketing, dans la langue courante.
 *
 * ⚠️ Pointe la page CONTACT, pas la page d'accueil. Deux raisons :
 *   1. le bouton s'appelle « Réserver votre démo » : atterrir sur l'accueil oblige à
 *      recliquer sur le CTA pour arriver au même endroit ;
 *   2. la page ouverte ici s'affiche DANS l'app (navigateur intégré), donc tout ce
 *      qu'elle contient est jugé par Apple. Le 20-08-2026, l'accueil portait un badge
 *      « Bientôt sur Google Play » qui a fait rejeter le build 25 (règle 2.3.10).
 * Les deux langues sont symétriques ici (`/fr/contact` et `/en/contact`), contrairement
 * à la politique de confidentialité juste en dessous.
 */
export const demoBookingUrl = (language: string): string =>
  `https://flowcutai.com/${language === "en" ? "en" : "fr"}/contact`;

/**
 * Politique de confidentialité, dans la langue courante.
 * ⚠️ Les chemins ne sont PAS symétriques entre les deux langues :
 *   FR → /fr/politiques      (marketing/src/pages/fr/politiques.astro)
 *   EN → /en/policies        (marketing/src/pages/en/policies.astro)
 * Écrire `/en/politiques` donnerait un 404.
 */
export const privacyPolicyUrl = (language: string): string =>
  language === "en"
    ? "https://flowcutai.com/en/policies"
    : "https://flowcutai.com/fr/politiques";
